import React, { useMemo, useState } from "react";

import AutoModeIcon from "@mui/icons-material/AutoMode";
import ChatIcon from "@mui/icons-material/Chat";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import TagIcon from "@mui/icons-material/Tag";
import TitleIcon from "@mui/icons-material/Title";
import WebIcon from "@mui/icons-material/Web";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { API_ERROR_MESSAGE } from "../../../../constants";
import { useUserState } from "../../../../store";
import { Category } from "../../../common/types";
import { RoleName } from "../../../common/enums/RoleName";
import CreditsWarningModal from "../../../common/CreditsWarningModal";
import { usersService } from "../../../common/Headerbar/services";
import {
  EPISODE_INSIGHT_CATEGORY_LABELS,
  getInsightCategoryLabel,
} from "../../constants";
import { episodeService } from "../../services";
import {
  InsightsResponse,
  RegenerateInsightsResponse,
} from "../../services/episode.service";
import RegenerateInsightsDialog from "./RegenerateInsightsModal";
import ChapterView from "./parts/ChapterView";
import ContentPost from "./parts/ContentPost";
import NewsletterPost from "./parts/NewsletterPost";
import Quote from "./parts/Quote";
import Title from "./parts/Title";

interface Props {
  insights?: InsightsResponse | undefined;
  refetch: () => void;
  episodeId: string;
}

type InsightCategoryKey = keyof InsightsResponse;
type InsightGroup = {
  id: string;
  label: string;
  helperText: string;
  categories: InsightCategoryKey[];
};

const CATEGORY_ORDER: InsightCategoryKey[] = [
  "quotes",
  "titles",
  "tweets",
  "chapters",
  "keywords",
  "blog_post",
  "newsletter",
  "linkedin_posts",
  "descriptions",
];

const INSIGHT_GROUPS: InsightGroup[] = [
  {
    id: "social-posts",
    label: "Social Posts",
    helperText: "Ready-to-publish content for social channels.",
    categories: ["tweets", "linkedin_posts", "quotes"],
  },
  {
    id: "publishing",
    label: "Publishing",
    helperText: "Assets to publish, promote, and package the episode.",
    categories: [
      "titles",
      "descriptions",
      "chapters",
      "keywords",
      "blog_post",
      "newsletter",
    ],
  },
];

const sectionIcons: Record<InsightCategoryKey, React.ElementType> = {
  tweets: ChatIcon,
  chapters: MenuBookIcon,
  blog_post: WebIcon,
  newsletter: NewspaperIcon,
  quotes: FormatQuoteIcon,
  linkedin_posts: LinkedInIcon,
  titles: TitleIcon,
  keywords: TagIcon,
  descriptions: DescriptionIcon,
};

const getCategoryCount = (
  category: InsightCategoryKey,
  insights?: InsightsResponse,
) => {
  if (!insights) return 0;

  if (category === "blog_post") return insights.blog_post ? 1 : 0;
  if (category === "newsletter") return insights.newsletter ? 1 : 0;
  return insights[category].length;
};

export default function Insights({ insights, refetch, episodeId }: Props) {
  const { user } = useUserState();
  const [selectedTab, setSelectedTab] = useState<InsightCategoryKey>("quotes");
  const [expandedSection, setExpandedSection] =
    useState<InsightCategoryKey | null>(null);
  const [isRegenerateInsightsModalOpen, setIsRegenerateInsightsModalOpen] =
    useState<boolean>(false);
  const [regeneratingInsightsForTab, setRegeneratingInsightsForTab] =
    useState<InsightCategoryKey | null>(null);
  const [creditsWarningModalOpen, setCreditsWarningModalOpen] =
    useState<boolean>(false);
  const [customPrompt, setCustomPrompt] = useState<string>("");

  const { data: userData } = useQuery({
    queryKey: ["authenticated-user"],
    queryFn: () => usersService.fetchAuthenticardUser(),
  });

  const isViewer = () => {
    if (!user || !Array.isArray(user.roles)) {
      return true;
    }
    return !user.roles.some(
      (role) => role === RoleName.ADMIN || role === RoleName.EDITOR,
    );
  };

  const availableCredits =
    userData?.organization?.subscription?.available_credits || 0;
  const hasCredits = availableCredits >= 5;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { mutate, isPending } = useMutation({
    mutationKey: ["regenerateInsights"],
    mutationFn: (values: RegenerateInsightsResponse) =>
      episodeService.regenerateInsights(episodeId, values),
    onSuccess: () => {
      refetch();
      setRegeneratingInsightsForTab(null);
      setIsRegenerateInsightsModalOpen(false);
      setCustomPrompt("");
    },
    onError: (err) => {
      if (err && typeof err === "object" && "statusCode" in err) {
        const apiError = err as {
          statusCode?: number;
          message?: string;
          error?: string;
        };
        if (apiError.statusCode === 429) {
          if (
            apiError.error ===
            API_ERROR_MESSAGE.MAXIMUM_NUMBER_OF_PROMPTS_REACHED
          ) {
            setCreditsWarningModalOpen(true);
          } else if (
            apiError.error === API_ERROR_MESSAGE.INSUFFICIENT_BALANCE
          ) {
            toast.error(`${apiError.error}: ${apiError.message}`);
          }
        }
      }
    },
  });

  const sections = useMemo(() => {
    return CATEGORY_ORDER.map((category) => ({
      category,
      count: getCategoryCount(category, insights),
    }));
  }, [insights]);

  const groupedSections = useMemo(() => {
    return INSIGHT_GROUPS.map((group) => {
      const groupSections = sections.filter((section) =>
        group.categories.includes(section.category),
      );
      const totalCount = groupSections.reduce(
        (sum, section) => sum + section.count,
        0,
      );

      return {
        ...group,
        sections: groupSections,
        totalCount,
      };
    });
  }, [sections]);

  const handleRegenerateInsights = (prompt?: string) => {
    setRegeneratingInsightsForTab(selectedTab);
    setCustomPrompt(prompt || "");
    mutate({
      category: selectedTab,
      custom_prompt: prompt,
    });
  };

  const handleReloadData = async () => {
    await refetch();
  };

  const handleConfirmCreditsWarning = () => {
    mutate({
      category: selectedTab,
      custom_prompt: customPrompt,
      allow_additional_charges: true,
    });
  };

  const renderEmptyState = (category: InsightCategoryKey) => (
    <Box
      sx={{
        width: "100%",
        borderRadius: 2,
        border: "1px dashed",
        borderColor: "divider",
        py: 4,
        px: 2,
        textAlign: "center",
        backgroundColor: "background.default",
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        No {getInsightCategoryLabel(category).toLowerCase()} yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Regenerate this section to create fresh content.
      </Typography>
    </Box>
  );

  const renderSectionContent = (category: InsightCategoryKey) => {
    if (isPending && regeneratingInsightsForTab === category) {
      return (
        <Box sx={{ display: "flex", py: 4, justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!insights) {
      return renderEmptyState(category);
    }

    if (category === "quotes") {
      if (!insights.quotes.length) return renderEmptyState(category);
      return insights.quotes.map((quoteObject) => (
        <Box key={quoteObject.timestamp} sx={{ width: "100%", mb: 2 }}>
          <Quote
            quoteObject={quoteObject}
            quotes={insights.quotes}
            refetch={refetch}
            category={Category.QUOTES}
          />
        </Box>
      ));
    }

    if (category === "titles") {
      if (!insights.titles.length) return renderEmptyState(category);
      return insights.titles.map((title) => (
        <Title
          key={title}
          category={Category.TITLES}
          title={title}
          titles={insights.titles}
          refetch={refetch}
        />
      ));
    }

    if (category === "tweets") {
      if (!insights.tweets.length) return renderEmptyState(category);
      return insights.tweets.map((tweet) => (
        <Title
          key={tweet}
          category={Category.TWEETS}
          title={tweet}
          titles={insights.tweets}
        />
      ));
    }

    if (category === "chapters") {
      if (!insights.chapters.length) return renderEmptyState(category);
      return insights.chapters.map((chapter) => (
        <ChapterView
          key={`${chapter.title}-${chapter.start}`}
          chapters={insights.chapters}
          chapterObject={chapter}
          category={Category.CHAPTERS}
          refetch={handleReloadData}
        />
      ));
    }

    if (category === "keywords") {
      if (!insights.keywords.length) return renderEmptyState(category);
      return insights.keywords.map((keyword) => (
        <Title
          key={keyword}
          category={Category.KEYWORDS}
          title={`#${keyword}`}
          titles={insights.keywords}
        />
      ));
    }

    if (category === "blog_post") {
      if (!insights.blog_post) return renderEmptyState(category);
      return (
        <ContentPost
          title={getInsightCategoryLabel("blog_post")}
          content={insights.blog_post}
          refetch={refetch}
          category={Category.BLOGPOST}
        />
      );
    }

    if (category === "newsletter") {
      if (!insights.newsletter) return renderEmptyState(category);
      return (
        <NewsletterPost
          title={getInsightCategoryLabel("newsletter")}
          content={insights.newsletter}
          refetch={refetch}
          category={Category.NEWSLETTER}
        />
      );
    }

    if (category === "linkedin_posts") {
      if (!insights.linkedin_posts.length) return renderEmptyState(category);
      return insights.linkedin_posts.map((linkedinPost) => (
        <Title
          key={linkedinPost}
          category={Category.LINKEDINPOSTS}
          title={linkedinPost}
          titles={insights.linkedin_posts}
        />
      ));
    }

    if (!insights.descriptions.length) return renderEmptyState(category);
    return insights.descriptions.map((description) => (
      <Title
        category={Category.DESCRIPTIONS}
        key={description}
        title={description}
        titles={insights.descriptions}
      />
    ));
  };

  return (
    <>
      <CreditsWarningModal
        open={creditsWarningModalOpen}
        onClose={() => setCreditsWarningModalOpen(false)}
        onConfirm={handleConfirmCreditsWarning}
      />
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflowY: "auto",
          pr: isMobile ? 0 : 0.5,
          pb: 1,
        }}
      >
        {groupedSections.map((group) => (
          <Box key={group.id} sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 1,
                px: 0.4,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "text.secondary",
                  }}
                >
                  {group.label}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  {group.helperText}
                </Typography>
              </Box>
              <Box
                sx={{
                  borderRadius: 999,
                  px: 1,
                  py: 0.25,
                  fontSize: 12,
                  fontWeight: 700,
                  backgroundColor:
                    group.totalCount > 0
                      ? "rgba(16, 185, 129, 0.12)"
                      : "rgba(107, 114, 128, 0.12)",
                  color: group.totalCount > 0 ? "#047857" : "text.secondary",
                }}
              >
                {group.totalCount}
              </Box>
            </Box>

            {group.sections.map((section) => {
              const Icon = sectionIcons[section.category];
              const isExpanded = expandedSection === section.category;

              return (
                <Box
                  key={section.category}
                  sx={{
                    mb: 1.5,
                    borderRadius: "14px",
                    border: "1px solid",
                    borderColor: isExpanded ? "primary.light" : "divider",
                    backgroundColor: "background.paper",
                    overflow: "hidden",
                    boxShadow: isExpanded
                      ? "0 8px 24px rgba(37, 99, 235, 0.10)"
                      : "none",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      setExpandedSection((prev) =>
                        prev === section.category ? null : section.category,
                      )
                    }
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.2,
                      px: isMobile ? 1.2 : 1.6,
                      py: 1.2,
                      border: "none",
                      backgroundColor: "transparent",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Icon
                        sx={{
                          fontSize: 18,
                          color: isExpanded ? "primary.main" : "text.secondary",
                        }}
                      />
                      <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
                        {EPISODE_INSIGHT_CATEGORY_LABELS[section.category]}
                      </Typography>
                      <Box
                        sx={{
                          borderRadius: 999,
                          px: 1,
                          py: 0.2,
                          fontSize: 12,
                          fontWeight: 600,
                          backgroundColor:
                            section.count > 0
                              ? "rgba(16, 185, 129, 0.12)"
                              : "rgba(107, 114, 128, 0.12)",
                          color:
                            section.count > 0 ? "#047857" : "text.secondary",
                        }}
                      >
                        {section.count}
                      </Box>
                    </Box>

                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
                    >
                      <Tooltip
                        title={
                          isViewer()
                            ? "Regenerate not available for viewer role"
                            : `Regenerate ${getInsightCategoryLabel(section.category)}`
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AutoModeIcon sx={{ fontSize: 16 }} />}
                            disabled={isViewer() || isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTab(section.category);
                              setExpandedSection(section.category);
                              setIsRegenerateInsightsModalOpen(true);
                            }}
                            sx={{
                              borderColor: "divider",
                              color: isViewer()
                                ? "text.disabled"
                                : "primary.main",
                              textTransform: "none",
                              fontSize: "12px",
                              fontWeight: 600,
                              px: 1,
                              minWidth: "auto",
                              "& .MuiButton-startIcon": {
                                mr: 0.4,
                              },
                              "&:hover": {
                                borderColor: "primary.light",
                                backgroundColor: isViewer()
                                  ? "transparent"
                                  : "action.hover",
                              },
                            }}
                          >
                            Regenerate
                          </Button>
                        </span>
                      </Tooltip>
                      <ExpandMoreIcon
                        sx={{
                          color: "text.secondary",
                          transform: isExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      />
                    </Box>
                  </Box>

                  {isExpanded && (
                    <Box
                      sx={{
                        px: isMobile ? 1 : 1.4,
                        pb: 1.2,
                        pt: 0.5,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "rgba(249, 250, 251, 0.45)",
                      }}
                    >
                      {renderSectionContent(section.category)}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
      <RegenerateInsightsDialog
        customPrompt={customPrompt}
        setCustomPrompt={setCustomPrompt}
        selectedTab={selectedTab}
        open={isRegenerateInsightsModalOpen}
        onClose={() => setIsRegenerateInsightsModalOpen(false)}
        onRegenerate={handleRegenerateInsights}
        hasCredits={hasCredits}
        loading={isPending}
      />
    </>
  );
}
